import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSubscription } from './useSubscription';
import { MS_PER_DAY } from '../constants/time';
import { supabase } from '../lib/supabase';
import { auth$ } from '../store/auth$';
import {
  canAccessFeature,
  getTierLimit,
  isMonthlyLimit,
  VIP_FEATURES,
  SOFT_GATE_FEATURES,
} from '@papper/shared/reedle/tiers';

/**
 * Map feature names to the limit key in tiers.ts and the operation in credit_usage_logs
 * EXPORTED_FOR_TESTING: quota-mapping-consistency.test.js cross-checks these
 * operation strings against the server/edge credit-logging code (ADR-008).
 */
export const FEATURE_LIMIT_MAP = {
  tts: { limitKey: 'ttsPerMonth', operationType: 'audio_generation' }, // server logs 'audio_generation'
  rag: { limitKey: 'ragSearchesPerMonth', operationType: 'rag_chat' }, // server logs 'rag_chat'
  pdf_upload: { limitKey: 'pdfUploadsPerMonth', operationType: 'pdf_processing' }, // server logs 'pdf_processing' per PDF (reedle-server articleController, always — even 0-cost)
  shared_lists: { limitKey: 'sharedListsTotal', operationType: null }, // counted differently
  article_storage: { limitKey: 'articleStorage', operationType: null },
};

/**
 * Calendar days until the trial ends, DST-safe: normalising both ends to local
 * midnight and rounding absorbs the ±1h a DST transition adds to a window —
 * ceil((end - now) / MS_PER_DAY) drifts a full day across a fall-back.
 */
const calendarDaysLeft = (trialEnd) => {
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  return Math.max(0, Math.round((startOfDay(trialEnd) - startOfDay(new Date())) / MS_PER_DAY));
};

/**
 * Feature access hook for gating features based on subscription tier and usage.
 *
 * Returns:
 * - allowed: boolean — can user use the feature right now?
 * - isInTrial: boolean — is user in Pro trial period?
 * - trialDaysLeft: number | null — days remaining in trial
 * - usageCount: number | null — current month usage (for usage-limited features)
 * - limit: number | null — tier limit (null = unlimited)
 * - remaining: number | null — remaining uses (null = unlimited)
 * - tierRequired: string — minimum tier needed for full access
 * - isSoftGate: boolean — feature is visible but locked (grayed + star)
 * - loading: boolean
 * - refresh: () => void — re-fetch usage data
 *
 * @param {string} feature - Feature key: 'tts', 'rag', 'pdf_upload', 'custom_fonts',
 *   'advanced_ai_models', 'mcp_api', 'shared_lists', 'article_storage'
 */
export function useFeatureAccess(feature) {
  const { subscription, loading: subLoading } = useSubscription();
  const [usageCount, setUsageCount] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // While subscription is genuinely unknown (loading + no cache), don't gate.
  // "Innocent until proven guilty" — server enforces actual limits anyway.
  const subUnknown = subLoading && subscription === null;

  const tier = subscription?.tier || 'free';

  // Trial state
  const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end) : null;
  const isInTrial = trialEnd ? trialEnd > new Date() : false;
  const trialDaysLeft = isInTrial ? calendarDaysLeft(trialEnd) : null;

  // During trial, user has tier='pro' from the DB, so canAccessFeature will pass.
  // We only need to enforce gates for free users AFTER trial.

  // Trial chip visibility: Day 7-20 of 30-day trial (trialDaysLeft 23..10)
  const showTrialChip = isInTrial && trialDaysLeft !== null && trialDaysLeft <= 23 && trialDaysLeft >= 10;

  // Determine limit for this feature
  const featureConfig = FEATURE_LIMIT_MAP[feature];
  const limit = featureConfig ? getTierLimit(tier, featureConfig.limitKey) : null;

  // Is this a VIP binary feature? (custom_fonts, mcp_api, advanced_ai_models)
  const isVipFeature = (VIP_FEATURES).includes(feature);
  const isSoftGate = (SOFT_GATE_FEATURES).includes(feature);

  // Fetch monthly usage count for usage-limited features
  const fetchUsage = useCallback(async () => {
    if (!featureConfig?.operationType) return;
    if (limit === null) {
      // Unlimited — no need to count
      setUsageCount(0);
      return;
    }

    setUsageLoading(true);
    try {
      const user = auth$.user.peek();
      if (!user) { setUsageLoading(false); return; }

      if (isMonthlyLimit(featureConfig.limitKey)) {
        // Query credit_usage_logs for current month count
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        const { count, error } = await supabase
          .from('reedle_credit_usage_logs')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('operation', featureConfig.operationType)
          .gte('created_at', startOfMonth.toISOString());

        if (!error) {
          setUsageCount(count || 0);
        }
      }
    } catch (err) {
      console.error(`[useFeatureAccess] Error fetching ${feature} usage:`, err);
    } finally {
      setUsageLoading(false);
    }
  }, [feature, featureConfig, limit]);

  useEffect(() => {
    if (!subLoading && featureConfig?.operationType) {
      fetchUsage();
    }
  }, [subLoading, fetchUsage]);

  // Compute access
  const result = useMemo(() => {
    // Subscription not yet loaded and no cache — don't gate anything.
    if (subUnknown) {
      return {
        allowed: true,
        isInTrial: false,
        showTrialChip: false,
        trialDaysLeft: null,
        usageCount: null,
        limit: null,
        remaining: null,
        tierRequired: 'free',
        isSoftGate: false,
        loading: true,
        refresh: fetchUsage,
      };
    }

    // VIP binary features (custom_fonts, mcp_api, advanced_ai_models)
    if (isVipFeature) {
      const allowed = canAccessFeature(tier, feature);
      return {
        allowed,
        isInTrial,
        showTrialChip,
        trialDaysLeft,
        usageCount: null,
        limit: null,
        remaining: null,
        tierRequired: 'pro',
        isSoftGate,
        loading: subLoading,
        refresh: fetchUsage,
      };
    }

    // Usage-limited features (tts, rag, pdf_upload)
    if (featureConfig) {
      const isUnlimited = limit === null;
      const currentUsage = usageCount ?? 0;
      const remaining = isUnlimited ? null : Math.max(0, limit - currentUsage);
      const allowed = isUnlimited || currentUsage < limit;

      return {
        allowed,
        isInTrial,
        showTrialChip,
        trialDaysLeft,
        usageCount: currentUsage,
        limit,
        remaining,
        tierRequired: 'pro',
        isSoftGate: false,
        loading: subLoading || usageLoading,
        refresh: fetchUsage,
      };
    }

    // Unknown feature — allow by default
    return {
      allowed: true,
      isInTrial,
      showTrialChip,
      trialDaysLeft,
      usageCount: null,
      limit: null,
      remaining: null,
      tierRequired: 'free',
      isSoftGate: false,
      loading: subLoading,
      refresh: fetchUsage,
    };
  }, [subUnknown, tier, isInTrial, showTrialChip, trialDaysLeft, isVipFeature, isSoftGate, featureConfig, limit, usageCount, subLoading, usageLoading, fetchUsage]);

  return result;
}

/**
 * Lightweight hook to just get trial status (for badges/banners).
 * Avoids usage queries.
 */
export function useTrialStatus() {
  const { subscription, loading } = useSubscription();

  const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end) : null;
  const isInTrial = trialEnd ? trialEnd > new Date() : false;
  const trialDaysLeft = isInTrial ? calendarDaysLeft(trialEnd) : null;
  const tier = subscription?.tier || 'free';
  const isTrialExpired = trialEnd && !isInTrial; // had trial but it ended
  // Trial chip visibility: Day 7-20 of 30-day trial (trialDaysLeft 23..10)
  const showTrialChip = isInTrial && trialDaysLeft !== null && trialDaysLeft <= 23 && trialDaysLeft >= 10;

  return { isInTrial, showTrialChip, trialDaysLeft, trialEnd, tier, isTrialExpired, loading };
}
